import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "@components/Button";
import { IconDisplay, IconSize, Icon } from "@components/Icon";
import { Loading } from "@components/LoadingIndicator";
import { Modal } from "@components/Modal";
import { FundraiserCard } from "@components/NewFundraiserCard";
import { Popover } from "@components/Popover";
import { PopoverItemButtonProps, PopoverList } from "@components/PopoverItem";
import { UUID } from "io-ts-types";
import React, { useState, useCallback, useMemo } from "react";

import {
  FundraiserResponse,
  NonprofitResponse,
} from "@every.org/common/src/codecs/entities";
import { DonationsTabQueryParam } from "@every.org/common/src/entity/types";
import {
  getRoutePath,
  URLFormat,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";

import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { useNonprofit } from "src/context/NonprofitsContext/hooks";
import { NonprofitFetchStatus } from "src/context/NonprofitsContext/types";
import { useNonprofitAdminData } from "src/hooks/useNonprofitAdminData";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { EndConfirmationModal } from "src/pages/Fundraiser/EditForm/EndConfirmationModal";
import { HideConfirmationModal } from "src/pages/Fundraiser/EditForm/HideConfirmationModal";
import {
  deleteFundraiser,
  hideFundraiser,
  unhideFundraiser,
  updateFundraiser,
} from "src/pages/Fundraiser/EditForm/actions";
import { canEditOrEndFundraiser } from "src/pages/Fundraiser/fundraiserPermissions";
import { verticalStackCss, horizontalStackCss } from "src/theme/spacing";
import { logger } from "src/utility/logger";

export const AdminFundraiserCard: React.FCC<{
  fundraiser: FundraiserResponse;
  onFundraiserDeleted: (id: UUID) => void;
  userId?: UUID;
  onPin?: (fundraiser: FundraiserResponse) => void;
}> = ({ fundraiser, onFundraiserDeleted, userId, onPin }) => {
  const nonprofit = useNonprofit({ id: fundraiser.nonprofitId });

  const [active, setActive] = useState(fundraiser.active);
  const [showEndModal, setShowEndModal] = useState(false);
  const [showHideModal, setShowHideModal] = useState(false);
  const loggedInUser = useLoggedInUserOrUndefined();
  const nonprofitAdmin = useNonprofitAdminData(fundraiser.nonprofitId);
  const canEditOrEnd = canEditOrEndFundraiser(
    fundraiser,
    loggedInUser,
    nonprofitAdmin
  );

  const endFundraiserAction = async () => {
    const updateBody = {
      active,
      title: fundraiser.title,
      description: fundraiser.title,
      coverImageCloudinaryId: fundraiser.coverImageCloudinaryId,
      creatorNonprofitId: fundraiser.creatorNonprofitId,
      goal: fundraiser.goalAmount,
    };
    try {
      if (fundraiser && updateBody) {
        updateBody.active = !active;
        await updateFundraiser(
          fundraiser.id,
          fundraiser.nonprofitId,
          updateBody
        );
        setActive(!active);
      }
    } catch (error) {
      logger.error({
        error,
        message: "Failed to end Fundraiser",
        data: { fundraiserId: fundraiser.id },
      });
    }
    setShowEndModal(false);
  };

  const hideFundraiserAction = async () => {
    try {
      if (fundraiser) {
        if (fundraiser.hidden) {
          const ret = await unhideFundraiser(
            fundraiser.id,
            fundraiser.nonprofitId
          );
          fundraiser.hidden = ret?.hidden;
        } else {
          const ret = await hideFundraiser(
            fundraiser.id,
            fundraiser.nonprofitId
          );
          fundraiser.hidden = ret?.hidden;
        }
      }
    } catch (error) {
      logger.error({
        error,
        message: "Failed to (un)hide Fundraiser",
        data: { fundraiserId: fundraiser.id, hidden: fundraiser.hidden },
      });
    }
    setShowHideModal(false);
  };

  if (nonprofit === NonprofitFetchStatus.FETCHING_NONPROFIT) {
    return <Loading />;
  }
  if (nonprofit === NonprofitFetchStatus.NONPROFIT_NOT_FOUND) {
    return null;
  }
  return (
    <div>
      <FundraiserCard
        fundraiser={fundraiser}
        nonprofit={nonprofit}
        showActions={false}
        showStatsForAdmin
        afterHeaderElement={
          <div css={{ marginLeft: "auto" }}>
            <FundraiserMoreButton
              fundraiser={fundraiser}
              nonprofit={nonprofit}
              onFundraiserDeleted={onFundraiserDeleted}
              userId={userId}
              canEditOrEnd={canEditOrEnd}
              active={active}
              setActive={setActive}
              setShowEndModal={setShowEndModal}
              setShowHideModal={setShowHideModal}
              onPin={onPin}
            />
          </div>
        }
      />
      <EndConfirmationModal
        isOpen={showEndModal}
        onReject={() => setShowEndModal(false)}
        nonprofitName={nonprofit.name}
        fundraiserId={fundraiser.id}
        nonprofitId={fundraiser.nonprofitId}
        isActive={active}
        onConfirm={endFundraiserAction}
      />
      <HideConfirmationModal
        isOpen={showHideModal}
        onReject={() => setShowHideModal(false)}
        nonprofitName={nonprofit.name}
        fundraiserId={fundraiser.id}
        nonprofitId={fundraiser.nonprofitId}
        hidden={fundraiser.hidden}
        onConfirm={hideFundraiserAction}
      />
    </div>
  );
};

const FundraiserMoreButton: React.FCC<{
  fundraiser: FundraiserResponse;
  nonprofit: NonprofitResponse;
  onFundraiserDeleted: (id: UUID) => void;
  userId?: UUID;
  canEditOrEnd?: boolean;
  canHideOrDelete?: boolean;
  isNonprofitAdmin?: boolean;
  onPin?: (fundraiser: FundraiserResponse) => void;
  active?: boolean;
  setActive: (end: boolean) => void;
  setShowEndModal: (end: boolean) => void;
  setShowHideModal: (end: boolean) => void;
}> = ({
  fundraiser,
  nonprofit,
  onFundraiserDeleted,
  userId,
  canEditOrEnd = false,
  onPin,
  active = true,
  setShowEndModal,
  setShowHideModal,
}) => {
  const [showDeleteFundraiser, setShowDeleteFundraiser] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const pinned =
    fundraiser.pinnedAt && fundraiser.pinnedAt > fundraiser.createdAt;

  const listItems = useMemo<PopoverItemButtonProps[]>(
    () => [
      {
        "data-tname": "nonprofitAdmin_fundraiserPinToTop",
        iconImport: () => import("@components/Icon/icons/PinIcon"),
        label: pinned ? "Unpin" : "Pin to top",
        hideItem: !onPin,
        onClick: {
          kind: ButtonTargetKind.FUNCTION,

          action: async (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowPopover(false);
            const response = await updateFundraiser(
              fundraiser.id,
              fundraiser.nonprofitId,
              {
                pinned: !pinned,
              }
            );
            onPin && onPin(response);
          },
        },
      },

      {
        "data-tname": "nonprofitAdmin_fundraiserEdit",
        iconImport: () => import("@components/Icon/icons/EditIcon"),
        label: "Edit",
        hideItem: !canEditOrEnd,
        onClick: {
          kind: ButtonTargetKind.LINK,
          to: getRoutePath({
            name: ClientRouteName.FUNDRAISER_EDIT,
            format: URLFormat.RELATIVE,
            tokens: {
              nonprofitSlug: nonprofit.primarySlug,
              fundraiserSlug: fundraiser.slug,
            },
          }),
        },
      },
      {
        "data-tname": "nonprofitAdmin_fundraiserEnd",
        iconImport: () =>
          active
            ? import("@components/Icon/icons/XIcon")
            : import("@components/Icon/icons/Rotate"),
        label: active ? "End" : "Resume",
        hideItem: !canEditOrEnd,
        onClick: {
          kind: ButtonTargetKind.FUNCTION,
          action: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowEndModal(true);
          },
        },
      },
      {
        "data-tname": "nonprofitAdmin_fundraiserHide",
        iconImport: () =>
          fundraiser.hidden
            ? import("@components/Icon/icons/EyeOffIcon")
            : import("@components/Icon/icons/EyeIcon"),
        label: fundraiser.hidden ? "Unhide" : "Hide",
        onClick: {
          kind: ButtonTargetKind.FUNCTION,
          action: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowHideModal(true);
          },
        },
      },
      {
        "data-tname": "nonprofitAdmin_fundraiserDelete",
        iconImport: () => import("@components/Icon/icons/XOctogonIcon"),
        label: "Delete",
        onClick: {
          kind: ButtonTargetKind.FUNCTION,
          action: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDeleteFundraiser(true);
          },
        },
      },
      {
        "data-tname": "nonprofitAdmin_fundraiserDonations",
        iconImport: () => import("@components/Icon/icons/SearchIcon"),
        label: "View donations",
        onClick: {
          kind: ButtonTargetKind.LINK,
          to: getRoutePath({
            name: ClientRouteName.NONPROFIT_ADMIN_DONATIONS,
            format: URLFormat.RELATIVE,
            tokens: {
              nonprofitSlug: nonprofit.primarySlug,
            },
            query: {
              [DonationsTabQueryParam.SEARCH_TERM]: fundraiser.slug,
            },
          }),
        },
      },
    ],
    [
      active,
      fundraiser.hidden,
      canEditOrEnd,
      fundraiser.id,
      fundraiser.nonprofitId,
      fundraiser.slug,
      nonprofit.primarySlug,
      onPin,
      pinned,
      setShowEndModal,
      setShowHideModal,
    ]
  );

  const moreUiRef = useOnClickOutside<HTMLUListElement>(
    useMemo(
      () => ({
        handleClickOutside() {
          setShowPopover(false);
        },
      }),
      []
    )
  );

  const onDelete = useCallback(async () => {
    setSubmitting(true);
    await deleteFundraiser(fundraiser.id, fundraiser.nonprofitId);
    onFundraiserDeleted(fundraiser.id);
    setShowDeleteFundraiser(false);
  }, [fundraiser.id, fundraiser.nonprofitId, onFundraiserDeleted]);

  return (
    <React.Fragment>
      <Modal
        isOpen={showDeleteFundraiser}
        contentLabel="Delete Fundraiser"
        onRequestClose={(e) => {
          e.preventDefault();
          setShowDeleteFundraiser(false);
        }}
      >
        <div css={[verticalStackCss.s, { maxWidth: "400px" }]}>
          <h3>
            {fundraiser.creatorNonprofitId ||
            (userId && userId === fundraiser.creatorUserId)
              ? "Delete your fundraiser?"
              : "Delete supporter’s fundraiser?"}
          </h3>
          {fundraiser.creatorNonprofitId ||
          (userId && userId === fundraiser.creatorUserId) ? (
            <p>
              This will NOT delete any donations already made through this
              fundraiser. {nonprofit.name} will still receive those funds.
            </p>
          ) : (
            <div css={verticalStackCss.l}>
              <p>
                This fundraiser is being run by one of your supporters. If you
                feel this fundraiser does not accurately represent your
                organization and its values you can delete it. The creator will
                be notified that you have chosen to delete the fundraiser.
              </p>
              <p>
                Note: This will NOT delete any donations already made through
                this fundraiser. You will still receive those funds.
              </p>
            </div>
          )}
          <div css={[horizontalStackCss.s, { justifyContent: "right" }]}>
            <Button
              css={{ display: "block" }}
              data-tname="keepAdminFundraiserButton"
              role={ButtonRole.SECONDARY}
              size={ButtonSize.SMALL}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: (e) => {
                  e.preventDefault();
                  setShowDeleteFundraiser(false);
                },
              }}
            >
              Keep
            </Button>
            <Button
              css={{ display: "block" }}
              data-tname="deleteAdminFundraiserButton"
              role={ButtonRole.PRIMARY}
              size={ButtonSize.SMALL}
              disabled={submitting}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete();
                },
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      <Popover
        visible={showPopover}
        placement={"bottom"}
        content={<PopoverList ref={moreUiRef} items={listItems} />}
      >
        <Button
          aria-label="Show more donation options"
          css={{ display: "block" }}
          data-tname="moreDonationButton"
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.SMALL}
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: (e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowPopover(true);
            },
          }}
        >
          <Icon
            iconImport={() => import("@components/Icon/icons/MoreIcon")}
            display={IconDisplay.ACCENT}
            size={IconSize.MEDIUM}
          />
        </Button>
      </Popover>
    </React.Fragment>
  );
};
